/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import SimpleTooltip from '../simple-tooltip';
import { sdgMap, SDGGoal, SDGTarget } from '../../constants/sdg-data';
import SDGIcon from '../sdg-icon/sdg-icon';
import './styles.scss';
import classNames from 'classnames';
import { MultiValue } from 'react-select';
import { TargetSelect } from './TargetSelect';

interface SDGPickerProps {
  selectedCodes: string[];
  includeTargets?: boolean;
  handleUpdate: (codes: string[]) => void;
  classes?: {
    label: string;
  }
}

interface SDGPickerButtonProps {
  isSelected: boolean;
  code: string;
  tooltip?: string;
  onClick: (code: string) => void;
}

export type SelectedTarget = {
  label: JSX.Element,
  value: string,
  goal: SDGGoal,
}

export type SelectedGoal = {
  label: string,
  value: string,
  options: SelectedTarget[],
}

const SDGPickerButton = ({ isSelected, tooltip, code, onClick }: SDGPickerButtonProps) => {
  return (
    <SimpleTooltip text={tooltip}>
      <Button color='link-secondary' onClick={() => onClick(code)}>
        <SDGIcon code={code} className={isSelected ? 'selected' : ''} width={'100%'} />
        {isSelected ? <i className='fa fa-check' /> : null}
      </Button>
    </SimpleTooltip>
  )
}

const getOptionLabel = (target: SDGTarget) => (
  <div>
    {target.code} - {target.shortName}
  </div>
);

const getSelectedGoals = (selectedCodes: string[]) =>
  sdgMap
    .filter((goal) => selectedCodes.includes(goal.code))
    .map((goal) => ({
      label: goal.shortName,
      value: goal.code,
      options: goal.targets.map((target) => ({ goal, label: getOptionLabel(target), value: target.code })),
    }));

const SdgPicker = ({ selectedCodes, handleUpdate, includeTargets, classes }: SDGPickerProps) => {
  const [selectedTargets, setSelectedTargets] = useState<SelectedTarget[]>([]);
  const selectedGoals = useMemo(() => getSelectedGoals(selectedCodes), [selectedCodes]);

  useEffect(() => {
    const targets = selectedGoals.map((goal) => goal.options.filter((target) => selectedCodes.includes(target.value)));
    setSelectedTargets(targets.flat());
  }, [selectedCodes, selectedGoals]);

  const handleToggleSelect = (code: string) => {
    let newCodes = [...selectedCodes];
    const foundCodeIndex = newCodes.findIndex(c => c === code);
    if (foundCodeIndex > -1) {
      newCodes.splice(foundCodeIndex, 1);
      newCodes = newCodes.filter(newCode => !newCode.startsWith(code));
    } else {
      newCodes.push(code);
    }
    handleUpdate(newCodes);
  }

  const onClick = (code: string) => handleToggleSelect(code);

  const handleOnChange = (selection: MultiValue<SelectedTarget>) => {
    setSelectedTargets(selection as SelectedTarget[]);
    const selectedTargetCodes = selectedTargets.map((item) => item.value);
    const goalCodes = selectedCodes.filter((code) => !selectedTargetCodes.includes(code));
    const newTargetCodes = selection.map((item) => item.value);
    handleUpdate([...goalCodes, ...newTargetCodes]);
  };

  return (
    <div className='sdg-picker'>
      <div className={classes?.label}>Related SDG Goals</div>
      <div className='mt-2 sdg-goals d-flex align-items-stretch'>
        {sdgMap.map(goal => {
          const isSelected = selectedCodes.includes(goal.code);
          return (
            <div key={`sdg-picker-g-${goal.code}`}>
              <SDGPickerButton tooltip={goal.shortName} onClick={onClick} isSelected={isSelected} code={goal.code} />
            </div>
          )
        })}
      </div>
      {includeTargets && (
        <div className='sdg-targets'>
          {selectedGoals.length > 0 ? (
            <div className='sdg-target-list'>
              <div className={classNames('mt-3 mb-2', classes?.label)}>Related SDG Targets</div>
              <TargetSelect selectedGoals={selectedGoals} selectedTargets={selectedTargets} handleOnChange={handleOnChange} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
export default SdgPicker;
